import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowDownRight,
  ArrowUpRight,
  Calendar,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amount);
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    weekday: "short",
  });
}

export function CashFlowManager() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Fetch projection
  const projection = useQuery(
    trpc.cashflow.getMonthlyProjection.queryOptions({
      month: selectedMonth,
      year: selectedYear,
      currentBalance: 0, // TODO: get from account balance
    })
  );

  // Generate month options (current month and next 12 months)
  const monthOptions = useMemo(() => {
    const options = [];
    const current = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(current.getFullYear(), current.getMonth() + i, 1);
      options.push({
        month: date.getMonth() + 1,
        year: date.getFullYear(),
        label: date.toLocaleDateString("pt-BR", {
          month: "long",
          year: "numeric",
        }),
      });
    }
    return options;
  }, []);

  // Loading state
  if (projection.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Carregando projeção...</div>
      </div>
    );
  }

  // Error state
  if (projection.isError) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>Erro ao carregar projeção de fluxo de caixa</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const data = projection.data;
  if (!data) {
    return null;
  }

  const isPositiveFlow = data.endingBalance > data.startingBalance;
  const netFlow = data.endingBalance - data.startingBalance;

  return (
    <div className="space-y-6">
      {/* Month Selector */}
      <div className="flex items-center gap-4">
        <Calendar className="h-5 w-5 text-muted-foreground" />
        <Select
          value={`${selectedYear}-${selectedMonth}`}
          onValueChange={(value) => {
            const [year, month] = value.split("-");
            setSelectedYear(Number.parseInt(year));
            setSelectedMonth(Number.parseInt(month));
          }}
        >
          <SelectTrigger className="w-[280px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((option) => (
              <SelectItem
                key={`${option.year}-${option.month}`}
                value={`${option.year}-${option.month}`}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {/* Starting Balance */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Saldo Inicial</CardTitle>
            <ArrowDownRight className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl">
              {formatCurrency(data.startingBalance)}
            </div>
            <p className="text-muted-foreground text-xs">Início do mês</p>
          </CardContent>
        </Card>

        {/* Ending Balance */}
        <Card
          className={
            data.endingBalance < 0
              ? "border-red-200 bg-red-50/50"
              : data.endingBalance < 1000
                ? "border-orange-200 bg-orange-50/50"
                : "border-green-200 bg-green-50/50"
          }
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Saldo Final Projetado</CardTitle>
            {isPositiveFlow ? (
              <TrendingUp className="h-4 w-4 text-green-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-600" />
            )}
          </CardHeader>
          <CardContent>
            <div
              className={`font-bold text-2xl ${
                data.endingBalance < 0
                  ? "text-red-600"
                  : data.endingBalance < 1000
                    ? "text-orange-600"
                    : "text-green-600"
              }`}
            >
              {formatCurrency(data.endingBalance)}
            </div>
            <p className="text-muted-foreground text-xs">
              {isPositiveFlow ? "+" : ""}
              {formatCurrency(netFlow)} vs início
            </p>
          </CardContent>
        </Card>

        {/* Lowest Balance */}
        <Card
          className={
            data.lowestBalance < 0
              ? "border-red-200 bg-red-50/50"
              : data.lowestBalance < 1000
                ? "border-orange-200 bg-orange-50/50"
                : ""
          }
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Menor Saldo</CardTitle>
            <AlertCircle
              className={`h-4 w-4 ${
                data.lowestBalance < 0
                  ? "text-red-600"
                  : data.lowestBalance < 1000
                    ? "text-orange-600"
                    : "text-muted-foreground"
              }`}
            />
          </CardHeader>
          <CardContent>
            <div
              className={`font-bold text-2xl ${
                data.lowestBalance < 0
                  ? "text-red-600"
                  : data.lowestBalance < 1000
                    ? "text-orange-600"
                    : ""
              }`}
            >
              {formatCurrency(data.lowestBalance)}
            </div>
            <p className="text-muted-foreground text-xs">
              {formatDate(data.lowestBalanceDate)}
            </p>
          </CardContent>
        </Card>

        {/* Critical Days */}
        <Card className={data.criticalDays > 0 ? "border-red-200 bg-red-50/50" : ""}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Dias Críticos</CardTitle>
            <AlertCircle
              className={`h-4 w-4 ${data.criticalDays > 0 ? "text-red-600" : "text-green-600"}`}
            />
          </CardHeader>
          <CardContent>
            <div
              className={`font-bold text-2xl ${data.criticalDays > 0 ? "text-red-600" : "text-green-600"}`}
            >
              {data.criticalDays}
            </div>
            <p className="text-muted-foreground text-xs">
              {data.criticalDays === 0
                ? "✅ Sem saldo negativo"
                : "⚠️ Dias com saldo negativo"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alert if critical days exist */}
      {data.criticalDays > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">
                  ⚠️ Atenção: {data.criticalDays} {data.criticalDays === 1 ? "dia" : "dias"} com
                  saldo negativo
                </h3>
                <p className="text-red-700 text-sm mt-1">
                  Seu saldo ficará negativo em alguns dias deste mês. O menor saldo será de{" "}
                  {formatCurrency(data.lowestBalance)} no dia {formatDate(data.lowestBalanceDate)}.
                  Considere adiar compras ou buscar renda extra.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Projection Table */}
      <Card>
        <CardHeader>
          <CardTitle>Projeção Dia a Dia</CardTitle>
          <CardDescription>
            Entradas, saídas e saldo projetado para cada dia do mês
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dia</TableHead>
                <TableHead className="text-right">Entradas</TableHead>
                <TableHead className="text-right">Saídas Fixas</TableHead>
                <TableHead className="text-right">Gastos Agendados</TableHead>
                <TableHead className="text-right">Gasto Estimado</TableHead>
                <TableHead className="text-right">Saldo Projetado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.projections.map((day) => (
                <TableRow
                  key={day.date}
                  className={
                    day.isCritical
                      ? "bg-red-50/50 dark:bg-red-950/20"
                      : day.isWarning
                        ? "bg-orange-50/50 dark:bg-orange-950/20"
                        : ""
                  }
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {formatDate(day.date)}
                      {day.hasLargeIncome && (
                        <Badge variant="outline" className="bg-green-100 text-green-700 text-xs">
                          💰 Entrada grande
                        </Badge>
                      )}
                      {day.hasLargeExpense && (
                        <Badge variant="outline" className="bg-red-100 text-red-700 text-xs">
                          💸 Saída grande
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {day.income > 0 ? (
                      <span className="text-green-600">+{formatCurrency(day.income)}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {day.fixedExpenses > 0 ? (
                      <span className="text-red-600">-{formatCurrency(day.fixedExpenses)}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {day.scheduledTransactions > 0 ? (
                      <span className="text-orange-600">
                        -{formatCurrency(day.scheduledTransactions)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground text-sm">
                    -{formatCurrency(day.estimatedDailySpending)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    <div className="flex items-center justify-end gap-2">
                      <span
                        className={
                          day.isCritical
                            ? "text-red-600"
                            : day.isWarning
                              ? "text-orange-600"
                              : "text-green-600"
                        }
                      >
                        {formatCurrency(day.projectedBalance)}
                      </span>
                      {day.isCritical && (
                        <Badge variant="destructive" className="text-xs">
                          ⚠️
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Total de Entradas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-green-600">
              {formatCurrency(data.totalIncome)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Total de Saídas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-red-600">
              {formatCurrency(data.totalExpenses + data.totalEstimatedSpending)}
            </div>
            <p className="text-muted-foreground text-xs">
              Fixas: {formatCurrency(data.totalExpenses)} + Estimadas:{" "}
              {formatCurrency(data.totalEstimatedSpending)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Fluxo Líquido</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`font-bold text-2xl ${isPositiveFlow ? "text-green-600" : "text-red-600"}`}
            >
              {isPositiveFlow ? "+" : ""}
              {formatCurrency(netFlow)}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
